import React, { useState, useEffect, useCallback } from 'react';
import './ImageConverter.css';
import { playSound } from '../../utils/sounds';
import { saveAs } from 'file-saver';
import mammoth from 'mammoth';
import { jsPDF } from 'jspdf';

// Impor suara-suara
import sndFileSelect from '../../sound/10_UI_Menu_SFX/013_Confirm_03.wav';
import sndMagicWarp from '../../sound/10_UI_Menu_SFX/051_use_item_01.wav';
import sndConvert from '../../sound/8_Atk_Magic_SFX/45_Charge_05.wav';
import sndFormatChange from '../../sound/10_UI_Menu_SFX/001_Hover_01.wav';
import sndReset from '../../sound/10_UI_Menu_SFX/029_Decline_09.wav';
import sndSuccess from '../../sound/SoupTonic UI1 SFX Pack 1 - mp3/SFX_UI_Saved.mp3';
import sndError from '../../sound/10_UI_Menu_SFX/033_Denied_03.wav';
import { motion, AnimatePresence } from 'framer-motion';
import loadingGif from '../../assets/transition.gif';

const Converter = ({ setIsTransitioning }) => {
  // State untuk konverter gambar
  const [selectedImage, setSelectedImage] = useState(null);
  const [outputFormat, setOutputFormat] = useState('jpeg');
  
  // State untuk konverter dokumen
  const [selectedDoc, setSelectedDoc] = useState(null);

  // State umum
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState(null);
  const [conversionResult, setConversionResult] = useState(null);

  useEffect(() => {
    if (setIsTransitioning) setIsTransitioning(false);
  }, [setIsTransitioning]);

  const handleImageFileChange = (event) => {
    playSound(sndFileSelect, 0.7);
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      resetState();
      setSelectedImage(file);
    } else {
      playSound(sndError);
      setError('Tolong pilih file gambar saja (JPG, PNG, dll).');
      resetState();
    }
  };

  const handleDocFileChange = (event) => {
      playSound(sndFileSelect, 0.7);
      const file = event.target.files[0];
      if (file && file.name.endsWith('.docx')) {
        resetState();
        setSelectedDoc(file);
      } else {
        playSound(sndError);
        setError('Tolong pilih file dokumen Word (.docx).');
        resetState();
      }
  };

  const handleFormatChange = (event) => {
    playSound(sndFormatChange, 0.5);
    setOutputFormat(event.target.value);
  };

  const handleImageConvert = () => {
    if (!selectedImage) return;
    
    // ... (logika konversi gambar yang ada tetap di sini, tapi kita akan ubah panggilannya)
    // ... kita sederhanakan untuk contoh ini
    convertFile('image');
  };

  const handleDocConvert = useCallback(() => {
    if (!selectedDoc) return;
    convertFile('document');
  }, [selectedDoc]);

  const convertFile = async (type) => {
      setIsConverting(true);
      setError(null);
      playSound(sndConvert);

      if (type === 'image') {
          // --- LOGIKA KONVERSI GAMBAR ---
          const reader = new FileReader();
          reader.readAsDataURL(selectedImage);
          reader.onload = (event) => {
              const img = new Image();
              img.src = event.target.result;
              img.onload = () => {
                  try {
                      const canvas = document.createElement('canvas');
                      canvas.width = img.width;
                      canvas.height = img.height;
                      const ctx = canvas.getContext('2d');
                      if (outputFormat === 'jpeg') {
                          ctx.fillStyle = 'white';
                          ctx.fillRect(0, 0, canvas.width, canvas.height);
                      }
                      ctx.drawImage(img, 0, 0);
                      canvas.toBlob((blob) => {
                          const newFileName = `${selectedImage.name.split('.').slice(0, -1).join('.')}.${outputFormat}`;
                          saveAs(blob, newFileName);
                          playSound(sndSuccess);
                          setConversionResult({ name: newFileName });
                          setIsConverting(false);
                          setSelectedImage(null);
                      }, `image/${outputFormat}`, 0.95);
                  } catch (e) {
                      handleConversionError(e.message);
                  }
              };
              img.onerror = () => handleConversionError('Gagal memuat gambar.');
          };
          reader.onerror = () => handleConversionError('Gagal membaca file.');
      } else if (type === 'document') {
          // --- LOGIKA KONVERSI DOKUMEN ---
          try {
              const arrayBuffer = await selectedDoc.arrayBuffer();
              const { value: html } = await mammoth.convertToHtml({ arrayBuffer });
              const doc = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
              const margin = { top: 40, right: 40, bottom: 40, left: 40 };
              const contentWidth = doc.internal.pageSize.getWidth() - margin.left - margin.right;
              await doc.html(html, {
                  callback: function (doc) {
                      const originalFileName = selectedDoc.name.substring(0, selectedDoc.name.lastIndexOf('.'));
                      doc.save(`${originalFileName}.pdf`);
                      playSound(sndSuccess);
                      setConversionResult({ name: `${originalFileName}.pdf` });
                      setIsConverting(false);
                      setSelectedDoc(null);
                  },
                  x: margin.left,
                  y: margin.top,
                  width: contentWidth,
                  windowWidth: contentWidth,
              });
          } catch (err) {
              handleConversionError('Gagal mengonversi dokumen.');
          }
      }
  };

  const handleConversionError = (message) => {
      playSound(sndError);
      setError(message);
      setIsConverting(false);
  }

  const resetState = () => {
      setSelectedImage(null);
      setSelectedDoc(null);
      setConversionResult(null);
      setError(null);
  }

  const renderInitialView = () => (
    <motion.div key="initial" className="initial-view" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        <p className="section-title">Konversi Gambar</p>
        <label className="dropzone image-dropzone">
            <div className="dropzone-icon image-icon"></div>
            <span>Pilih File Gambar</span>
            <input type="file" onChange={handleImageFileChange} className="file-input-hidden" accept="image/*" />
        </label>
        
        <div className="divider-or">ATAU</div>

        <p className="section-title">Konversi Dokumen</p>
        <label className="dropzone doc-dropzone">
            <div className="dropzone-icon doc-icon"></div>
            <span>Pilih File .docx</span>
            <input type="file" onChange={handleDocFileChange} className="file-input-hidden" accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx" />
        </label>
    </motion.div>
  );

  const renderSelectedFileView = () => (
     <motion.div key="file-info" className="file-info" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        {selectedImage && (
            <>
                <p>Siap Konversi Gambar: <strong>{selectedImage.name}</strong></p>
                <div className="converter-controls">
                    <select value={outputFormat} onChange={handleFormatChange} className="format-select" disabled={isConverting}>
                        <option value="jpeg">ke JPG</option>
                        <option value="png">ke PNG</option>
                        <option value="webp">ke WebP</option>
                    </select>
                    <button onClick={() => convertFile('image')} className="convert-button" disabled={isConverting}>
                        {isConverting ? 'Mengonversi...' : 'Konversi Gambar!'}
                    </button>
                </div>
            </>
        )}
        {selectedDoc && (
             <>
                <p>Siap Konversi Dokumen: <strong>{selectedDoc.name}</strong></p>
                <div className="converter-controls">
                    <button onClick={() => convertFile('document')} className="convert-button" disabled={isConverting}>
                        {isConverting ? 'Mengonversi...' : 'Konversi ke PDF!'}
                    </button>
                </div>
            </>
        )}
         <button onClick={resetState} className="change-file-button" disabled={isConverting}>Batal</button>
      </motion.div>
  );

  const renderResultView = () => (
    <motion.div key="result" className="file-info result" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <p><strong>Berhasil!</strong> File {conversionResult.name} seharusnya sudah terunduh.</p>
        <button onClick={resetState} className="action-button">
          Konversi File Lain
        </button>
    </motion.div>
  );

  return (
    <motion.div className="converter-container" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <AnimatePresence>
        {isConverting && (
          <motion.div
            className="loading-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <img src={loadingGif} alt="Animasi Konversi" className="loading-animation"/>
            <p className="progress-text">Mengubah wujud...</p>
          </motion.div>
        )}
      </AnimatePresence>

      <h2>Konverter File Ajaib</h2>
      <p>Ubah format gambar atau ubah .docx ke PDF!</p>

      <AnimatePresence mode="wait">
        {conversionResult ? renderResultView() : (selectedImage || selectedDoc ? renderSelectedFileView() : renderInitialView())}
      </AnimatePresence>

      <AnimatePresence>
        {error && (
            <motion.div className="message-box error" initial={{y: -10, opacity: 0}} animate={{y: 0, opacity: 1}} exit={{y: -10, opacity: 0}}>
                {error}
            </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Converter;